import CancellableHelper from "@/requests/CancellableHelper";
import GenericTableRequest from "@/models/api/GenericTableRequest";
import {
  CostDocumentOtherFiltered,
  CostDocumentOtherResponse,
} from "@/models/api/costDocumentsOther/CostDocumentOther";
import { CostDocumentOtherItem } from "@/models/api/costDocumentsOther/CostDocumentOtherItem";
import { FileGuid } from "@/models/api/Files";
import { DoubleConfirm } from "@/models/api/DoubleConfirm";
import { CostDocumentDetails } from "@/models/api/costDocuments/CostDocumentDetails";
import { CostDocumentOtherViewModel } from "@/models/api/costDocumentsOther/editor/CostDocumentOtherViewModel";
import { CostDocumentOtherItemViewModel } from "@/models/api/costDocumentsOther/editor/CostDocumentOtherItemViewModel";
import { FileViewModel } from "@/models/api/costDocumentsOther/editor/FileViewModel";
import { ContractorViewModel } from "@/models/api/costDocumentsOther/editor/ContractorViewModel";
import { AttributeItem } from "@/models/api/bookings/BookingGetTemporaryItem";
import BaseCommand from "@/models/api/commands/BaseCommand";
import { OperationResult } from "@/models/api/orders/OperationResult";
import { axiosInstance } from "@/plugins/axios";
import { SelectItem } from "@/models/api/SelectItem";
import { CONTRACTOR_TYPE_ENUM } from "@/static/contractorTypeEnum";
import { COST_DOCUMENT_TYPES } from "@/static/costDocumentTypes";

//Routes
export const URL_GET_COST_DOCUMENTS_OTHER = "/CostDocumentOther";
export const URL_GET_COST_DOCUMENT_OTHER_ITEMS = "/CostDocumentOther/${CostDocumentId}/Items";
export const URL_GET_COST_DOCUMENT_OTHER_FILES = "/CostDocumentOther/${CostDocumentId}/Files";
export const URL_GET_COST_DOCUMENT_OTHER_DETAILS = "/CostDocumentOther/${CostDocumentId}/Details";
export const URL_POST_REMOVE_COST_DOCUMENT_OTHER = "/CostDocumentOther/${CostDocumentId}/Remove";
export const URL_POST_APPROVE_COST_DOCUMENT_OTHER = "/CostDocumentOther/${CostDocumentId}/Approve";
export const URL_POST_APPROVE_COST_DOCUMENTS_OTHER = "/CostDocumentOther/Approve";
export const URL_POST_CHANGE_PAYER = "/CostDocumentOther/ChangePayer";
export const URL_POST_CHANGE_CATEGORY = "/CostDocumentOther/ChangeCategory";
export const URL_POST_SET_STATUS_TO_SCAM = "/CostDocumentOther/${CostDocumentId}/Scam";
export const URL_POST_CANCEL_SCAM_STATUS = "/CostDocumentOther/${CostDocumentId}/CancelScam";
export const URL_GET_COST_DOCUMENTS_OTHER_FILTERED = "/CostDocumentOther/Filtered";

export const URL_POST_ADD_COST_DOCUMENT_OTHER = "/CostDocumentOther/AddCostDocumentOther/${Type}";
export const URL_POST_EDIT_COST_DOCUMENT_OTHER = "/CostDocumentOther/EditCostDocumentOther/${CostDocumentId}";
export const URL_GET_TEMPORARY_COST_DOCUMENT_OTHER = "/CostDocumentOther/TemporaryCostDocumentOther/${OperationId}";
export const URL_GET_TEMPORARY_COST_DOCUMENT_OTHER_ITEMS =
  "/CostDocumentOther/TemporaryCostDocumentOtherItems/${OperationId}";
export const URL_GET_TEMPORARY_FILES = "/CostDocumentOther/TemporaryFiles/${OperationId}";
export const URL_GET_TEMPORARY_CONTRACTOR = "/CostDocumentOther/TemporaryContractor/${OperationId}/${Type}";
export const URL_GET_TEMPORARY_ATTRIBUTES = "/CostDocumentOther/TemporaryAttributes/${OperationId}/${TmpId}";
export const URL_GET_PAYER_BANK_ACCOUNT = "/CostDocumentOther/PayerBankAccount/${OperationId}";
export const URL_GET_COST_DOCUMENT_OTHER_TYPES = "/CostDocumentOther/CostDocumentOtherTypes";
export const URL_POST_UPDATE_COST_DOCUMENT_OTHER_TEMPORARY = "/CostDocumentOther/UpdateCostDocumentOtherTemporary";
export const URL_POST_SAVE_COST_DOCUMENT_OTHER = "/CostDocumentOther/SaveCostDocumentOther/${OperationId}";
export const URL_POST_UPLOAD_TEMPORARY_FILES = "/CostDocumentOther/UploadTemporaryFiles/${OperationId}";
export const URL_GET_BANK_ACCOUNTS = "/CostDocumentOther/BankAccounts";

//Requests
const _getCostDocumentsOther = new CancellableHelper<CostDocumentOtherResponse>(
  URL_GET_COST_DOCUMENTS_OTHER,
  CostDocumentOtherResponse
);
_getCostDocumentsOther.jsonSerializer = true;
export const getCostDocumentsOther = async (
  genericTableRequest: GenericTableRequest
): Promise<CostDocumentOtherResponse> => {
  return await _getCostDocumentsOther.get(genericTableRequest);
};

const _getCostDocumentOtherItems = new CancellableHelper<Array<CostDocumentOtherItem>>(
  URL_GET_COST_DOCUMENT_OTHER_ITEMS,
  CostDocumentOtherItem
);
export const getCostDocumentOtherItems = async (costDocumentId: string): Promise<Array<CostDocumentOtherItem>> => {
  return await _getCostDocumentOtherItems.get({ CostDocumentId: costDocumentId });
};

const _getCostDocumentOtherFiles = new CancellableHelper<Array<FileGuid>>(URL_GET_COST_DOCUMENT_OTHER_FILES, FileGuid);
export const getCostDocumentOtherFiles = async (costDocumentId: string): Promise<Array<FileGuid>> => {
  return await _getCostDocumentOtherFiles.get({ CostDocumentId: costDocumentId });
};

const _getCostDocumentOtherDetails = new CancellableHelper<CostDocumentDetails>(
  URL_GET_COST_DOCUMENT_OTHER_DETAILS,
  CostDocumentDetails
);
export const getCostDocumentOtherDetails = async (costDocumentId: string): Promise<CostDocumentDetails> => {
  return await _getCostDocumentOtherDetails.get({ CostDocumentId: costDocumentId });
};

const _removeCostDocumentOther = new CancellableHelper<DoubleConfirm>(URL_POST_REMOVE_COST_DOCUMENT_OTHER, null, true);
export const removeCostDocumentOther = async (costDocumentId: string, confirmed: boolean): Promise<DoubleConfirm> => {
  return await _removeCostDocumentOther.post({
    CostDocumentId: costDocumentId,
    Confirmed: confirmed,
  });
};

const _approveCostDocumentOther = new CancellableHelper<string>(URL_POST_APPROVE_COST_DOCUMENT_OTHER);
export const approveCostDocumentOther = async (costDocumentId: string): Promise<string> => {
  return await _approveCostDocumentOther.post({ CostDocumentId: costDocumentId });
};

const _approveCostDocumentsOther = new CancellableHelper<string>(URL_POST_APPROVE_COST_DOCUMENTS_OTHER);
export const approveCostDocumentsOther = async (costDocumentIds: Array<string>): Promise<string> => {
  return await _approveCostDocumentsOther.post({}, costDocumentIds);
};

const _changePayer = new CancellableHelper<string>(URL_POST_CHANGE_PAYER);
export const changePayer = async (contractorId: string | null, costDocumentIds: Array<string>): Promise<string> => {
  return await _changePayer.post({}, { ContractorId: contractorId, CostDocumentIds: costDocumentIds });
};

const _changeCategory = new CancellableHelper<string>(URL_POST_CHANGE_CATEGORY);
export const changeCategory = async (serviceTypeId: number, costDocumentIds: Array<string>): Promise<string> => {
  return await _changeCategory.post({}, { ServiceTypeId: serviceTypeId, CostDocumentIds: costDocumentIds });
};

const _setStatusToScam = new CancellableHelper<string>(URL_POST_SET_STATUS_TO_SCAM);
export const setStatusToScam = async (costDocumentId: string, reason: string): Promise<string> => {
  return await _setStatusToScam.post({ CostDocumentId: costDocumentId }, reason);
};

const _cancelScamStatus = new CancellableHelper<string>(URL_POST_CANCEL_SCAM_STATUS);
export const cancelScamStatus = async (costDocumentId: string, reason: string): Promise<string> => {
  return await _cancelScamStatus.post({ CostDocumentId: costDocumentId }, reason);
};

const _getCostDocumentsOtherFiltered = new CancellableHelper<Array<CostDocumentOtherFiltered>>(
  URL_GET_COST_DOCUMENTS_OTHER_FILTERED,
  CostDocumentOtherFiltered
);
_getCostDocumentsOtherFiltered.jsonSerializer = true;
export const getCostDocumentsOtherFiltered = async (
  genericTableRequest: GenericTableRequest
): Promise<Array<CostDocumentOtherFiltered>> => {
  return await _getCostDocumentsOtherFiltered.get(genericTableRequest);
};

const _editCostDocumentOther = new CancellableHelper<string>(URL_POST_EDIT_COST_DOCUMENT_OTHER);
export const editCostDocumentOther = async (costDocumentId: string): Promise<string> => {
  return await _editCostDocumentOther.post({ CostDocumentId: costDocumentId });
};

const _addCostDocumentOther = new CancellableHelper<string>(URL_POST_ADD_COST_DOCUMENT_OTHER);
export const addCostDocumentOther = async (type: COST_DOCUMENT_TYPES): Promise<string> => {
  return await _addCostDocumentOther.post({ Type: type });
};

const _getTemporaryCostDocumentOther = new CancellableHelper<CostDocumentOtherViewModel>(
  URL_GET_TEMPORARY_COST_DOCUMENT_OTHER,
  CostDocumentOtherViewModel
);
export const getTemporaryCostDocumentOther = async (operationId: string): Promise<CostDocumentOtherViewModel> => {
  return await _getTemporaryCostDocumentOther.get({ OperationId: operationId });
};

const _getTemporaryCostDocumentOtherItems = new CancellableHelper<Array<CostDocumentOtherItemViewModel>>(
  URL_GET_TEMPORARY_COST_DOCUMENT_OTHER_ITEMS,
  CostDocumentOtherItemViewModel
);
export const getTemporaryCostDocumentOtherItems = async (
  operationId: string
): Promise<Array<CostDocumentOtherItemViewModel>> => {
  return await _getTemporaryCostDocumentOtherItems.get({ OperationId: operationId });
};

const _getTemporaryFiles = new CancellableHelper<Array<FileViewModel>>(URL_GET_TEMPORARY_FILES, FileViewModel);
export const getTemporaryFiles = async (operationId: string): Promise<Array<FileViewModel>> => {
  return await _getTemporaryFiles.get({ OperationId: operationId });
};

const _getTemporaryContractor = new CancellableHelper<ContractorViewModel>(
  URL_GET_TEMPORARY_CONTRACTOR,
  ContractorViewModel
);
export const getTemporaryContractor = async (
  operationId: string,
  type: CONTRACTOR_TYPE_ENUM
): Promise<ContractorViewModel> => {
  return await _getTemporaryContractor.get({ OperationId: operationId, Type: type });
};

const _getTemporaryAttributes = new CancellableHelper<Array<AttributeItem>>(
  URL_GET_TEMPORARY_ATTRIBUTES,
  AttributeItem
);
export const getTemporaryAttributes = async (
  operationId: string,
  tmpId: string | null
): Promise<Array<AttributeItem>> => {
  return await _getTemporaryAttributes.get({ OperationId: operationId, TmpId: tmpId });
};

const _getPayerBankAccounts = new CancellableHelper<Array<string>>(URL_GET_PAYER_BANK_ACCOUNT, "string");
export const getPayerBankAccounts = async (operationId: string): Promise<Array<string>> => {
  return await _getPayerBankAccounts.get({ OperationId: operationId });
};

const _getCostDocumentOtherTypes = new CancellableHelper<Array<SelectItem>>(
  URL_GET_COST_DOCUMENT_OTHER_TYPES,
  SelectItem
);
export const getCostDocumentOtherTypes = async (): Promise<Array<SelectItem>> => {
  return await _getCostDocumentOtherTypes.get();
};

const _updateCostDocumentOtherTemporary = new CancellableHelper<OperationResult>(
  URL_POST_UPDATE_COST_DOCUMENT_OTHER_TEMPORARY,
  OperationResult
);
export const updateCostDocumentOtherTemporary = async (command: BaseCommand): Promise<OperationResult> => {
  return await _updateCostDocumentOtherTemporary.post(command);
};

const _saveCostDocumentOther = new CancellableHelper<string>(URL_POST_SAVE_COST_DOCUMENT_OTHER);
export const saveCostDocumentOther = async (operationId: string): Promise<string> => {
  return await _saveCostDocumentOther.post({ OperationId: operationId });
};

export const postUploadTemporaryFiles = async (operationId: string, files: Array<File>): Promise<OperationResult> => {
  const url = URL_POST_UPLOAD_TEMPORARY_FILES.replace("${OperationId}", operationId);
  const formData = new FormData();
  for (const file of files) formData.append(file.name, file);
  return (
    await axiosInstance.post(url, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
  ).data as OperationResult;
};

const _getBankAccounts = new CancellableHelper<Array<SelectItem>>(URL_GET_BANK_ACCOUNTS);
export const getBankAccounts = async (): Promise<Array<SelectItem>> => {
  return _getBankAccounts.get();
};
